import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Mail, Phone, Clock } from "lucide-react";
import { submitContactForm, type ContactAttachment } from "@/lib/contact-submit";
import { AttachmentField } from "@/components/sections/AttachmentField";

interface Props {
  title?: string;
  subtitle?: string;
  compact?: boolean;
  office?: string;
}

export const ContactForm = ({
  title = "Kontakta oss för kostnadsfri rådgivning",
  subtitle = "Fyll i formuläret så återkommer vi inom kort.",
  compact = false,
  office,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [attachments, setAttachments] = useState<ContactAttachment[]>([]);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    setSubmitted(false);
    setLoading(true);

    try {
      await submitContactForm({
        name: String(formData.get("name") || ""),
        email: String(formData.get("email") || ""),
        phone: String(formData.get("phone") || ""),
        company: String(formData.get("company") || ""),
        message: String(formData.get("message") || ""),
        office: String(formData.get("office") || office || ""),
        source: title,
        page: typeof window !== "undefined" ? window.location.href : undefined,
        attachments,
      });
      setSubmitted(true);
      toast.success("Tack! Vi har tagit emot ditt meddelande.");
      form.reset();
      setAttachments([]);
    } catch (error) {
      console.error("[contact] submission failed", error);
      setSubmitted(false);
      toast.error(error instanceof Error ? error.message : "Meddelandet kunde inte skickas. Mejla oss gärna direkt.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className={compact ? "py-16" : "py-24 md:py-32 bg-background-deep border-y border-border"}>
      <div className="container-page">
        <div className="grid gap-12 md:grid-cols-12 items-center">
          <div className="md:col-span-5">
            <div className="eyebrow">— Kontakt</div>
            <h2 className="display-md mt-6 text-foreground text-balance">{title}</h2>
            <p className="lead mt-6">{subtitle}</p>
            <div className="mt-10 space-y-5">
              <a href="tel:0703036345" className="flex items-center gap-4 group">
                <div className="h-11 w-11 rounded-full bg-card border border-border flex items-center justify-center group-hover:border-primary/40 transition-colors">
                  <Phone className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-foreground-muted">Ring oss</div>
                  <div className="text-foreground font-medium">070-303 63 45</div>
                </div>
              </a>
              <a href="mailto:info@cloudredovisning.se" className="flex items-center gap-4 group">
                <div className="h-11 w-11 rounded-full bg-card border border-border flex items-center justify-center group-hover:border-primary/40 transition-colors">
                  <Mail className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-foreground-muted">Mejla oss</div>
                  <div className="text-foreground font-medium">info@cloudredovisning.se</div>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <div className="h-11 w-11 rounded-full bg-card border border-border flex items-center justify-center">
                  <Clock className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-foreground-muted">Svarstid</div>
                  <div className="text-foreground font-medium">Inom 24 timmar, vardagar</div>
                </div>
              </div>
            </div>
          </div>
          <div className="md:col-span-7">
            <form onSubmit={onSubmit} className="space-y-5 rounded-sm border border-border bg-card p-8 md:p-10 shadow-soft">
              {office ? <input type="hidden" name="office" value={office} /> : null}
              {submitted ? (
                <div
                  role="status"
                  aria-live="polite"
                  className="rounded-sm border border-primary/25 bg-primary/10 px-5 py-4 text-sm text-foreground"
                >
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                    <div>
                      <div className="font-medium">Tack för ditt meddelande.</div>
                      <div className="mt-1 text-foreground-muted">Vi har tagit emot din förfrågan och återkommer inom kort.</div>
                    </div>
                  </div>
                </div>
              ) : null}
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Namn" name="name" autoComplete="name" required />
                <Field label="E-post" name="email" type="email" autoComplete="email" required />
              </div>
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Telefon" name="phone" type="tel" autoComplete="tel" />
                <Field label="Företag" name="company" autoComplete="organization" />
              </div>
              <Field label="Meddelande" name="message" textarea />
              <AttachmentField files={attachments} onChange={setAttachments} disabled={loading} compact={compact} />
              <p className="text-xs text-foreground-muted leading-relaxed">
                Genom att skicka samtycker du till att vi behandlar dina personuppgifter för att besvara din fråga.
              </p>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-3 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground transition-all hover:bg-foreground disabled:opacity-60"
              >
                {loading ? "Skickar..." : "Skicka meddelande"}
                <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({
  label,
  name,
  type = "text",
  textarea,
  required,
  autoComplete,
}: {
  label: string;
  name: string;
  type?: string;
  textarea?: boolean;
  required?: boolean;
  autoComplete?: string;
}) => (
  <label className="block group">
    <span className="text-xs uppercase tracking-[0.18em] text-foreground-muted">{label}{required && " *"}</span>
    {textarea ? (
      <textarea
        name={name}
        rows={5}
        required={required}
        className="mt-2 w-full rounded-sm border border-border bg-background px-4 py-3 text-foreground placeholder:text-foreground-muted/50 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-colors resize-none"
      />
    ) : (
      <input
        type={type}
        name={name}
        required={required}
        autoComplete={autoComplete}
        className="mt-2 w-full rounded-sm border border-border bg-background px-4 py-3 text-foreground placeholder:text-foreground-muted/50 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-colors"
      />
    )}
  </label>
);
